import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send, Loader2, CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/i18n";

const CONTACT_EMAIL = "a.alarifi@a-narch.com";

export const ContactFormModal = ({ open, onClose }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setErrorMsg(t("fillRequired"));
      setStatus("error");
      return;
    }
    setStatus("sending");
    setErrorMsg("");
    try {
      const subject = encodeURIComponent(form.subject.trim() || `Portfolio message from ${form.name.trim()}`);
      const body = encodeURIComponent(
        [
          form.message.trim(),
          "",
          `Name: ${form.name.trim()}`,
          `Email: ${form.email.trim()}`,
        ].join("\n")
      );

      window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
      setStatus("sent");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(t("emailError"));
    }
  };

  const handleClose = () => {
    setStatus("idle");
    setErrorMsg("");
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="contact-form-overlay"
          initial={{ opacity: 0, pointerEvents: "auto" }}
          animate={{ opacity: 1, pointerEvents: "auto" }}
          exit={{ opacity: 0, pointerEvents: "none", transition: { duration: 0.15 } }}
          onClick={handleClose}
          className="fixed inset-0 z-[110] grid place-items-center px-4"
          style={{ background: "rgba(40,35,25,0.4)", backdropFilter: "blur(3px)" }}
        >
          <motion.div
            data-testid="contact-form-sheet"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.94, y: 20, rotate: -1 }}
            animate={{ opacity: 1, scale: 1, y: 0, rotate: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 10 }}
            transition={{ type: "spring", stiffness: 200, damping: 22 }}
            className="paper paper-corner-fold relative w-full max-w-md"
          >
            <span className="tape" style={{ top: -10, left: 30, transform: "rotate(-5deg)" }} aria-hidden />
            <span className="tape" style={{ top: -10, right: 30, transform: "rotate(4deg)" }} aria-hidden />

            <button
              data-testid="btn-close-contact-form"
              onClick={handleClose}
              aria-label={t("closeForm")}
              className="absolute top-4 right-4 h-8 w-8 grid place-items-center rounded-full text-ink-faint hover:text-accentRed hover:bg-[var(--bg-board-2)] transition"
            >
              <X size={16} />
            </button>

            <div className="px-7 pt-9 pb-7">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-faint">{t("notePinned")}</p>
              <h2 className="font-display text-2xl font-medium tracking-tight text-ink mt-1">{t("leaveNote")}</h2>
              <p className="text-sm text-ink-soft mt-2 leading-relaxed">
                {t("noteIntro")}
              </p>

              {status === "sent" ? (
                <div data-testid="contact-form-success" className="mt-6 border border-dashed border-[var(--paper-edge)] p-5 text-center">
                  <CheckCircle2 size={28} className="mx-auto text-accentRed" />
                  <p className="font-display text-lg mt-2 text-ink">{t("noteReceived")}</p>
                  <p className="font-hand text-xl text-accentBlue mt-1 rotate-[-2deg] inline-block">{t("thankYou")}</p>
                  <button
                    data-testid="btn-send-another"
                    onClick={() => setStatus("idle")}
                    className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-ink hover:text-accentRed underline-offset-4 hover:underline transition"
                  >
                    {t("sendAnother")}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="mt-5 space-y-4" data-testid="contact-form">
                  <Field label={t("name")} testId="contact-input-name" value={form.name} onChange={update("name")} required />
                  <Field label={t("email")} type="email" testId="contact-input-email" value={form.email} onChange={update("email")} required />
                  <Field label={t("subject")} testId="contact-input-subject" value={form.subject} onChange={update("subject")} />
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ink-faint mb-1">
                      {t("message")}
                    </label>
                    <textarea
                      data-testid="contact-input-message"
                      required
                      rows={4}
                      value={form.message}
                      onChange={update("message")}
                      className="w-full bg-transparent border-b border-[var(--paper-edge)] focus:border-ink outline-none text-sm text-ink py-1.5 resize-none"
                    />
                  </div>

                  {errorMsg && (
                    <p data-testid="contact-form-error" className="font-mono text-[11px] text-accentRed">
                      {errorMsg}
                    </p>
                  )}

                  <button
                    data-testid="btn-submit-contact"
                    type="submit"
                    disabled={status === "sending"}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-3 bg-accentRed text-paper hover:bg-ink transition font-mono text-[10px] uppercase tracking-[0.22em] disabled:opacity-60"
                  >
                    {status === "sending" ? (<><Loader2 size={13} className="animate-spin" /> {t("sending")}</>) : (<><Send size={13} /> {t("sendNote")}</>)}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Field = ({ label, type = "text", value, onChange, testId, required }) => (
  <div>
    <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ink-faint mb-1">
      {label}{required ? " *" : ""}
    </label>
    <input
      data-testid={testId}
      type={type}
      value={value}
      required={required}
      onChange={onChange}
      className="w-full bg-transparent border-b border-[var(--paper-edge)] focus:border-ink outline-none text-sm text-ink py-1.5"
    />
  </div>
);

export default ContactFormModal;
